import { type ContextType, type ReactNode, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PresenceContext } from 'framer-motion';

// A presence context with `initial: false` makes every motion component below it
// start in its final state instead of playing an entrance animation.
const NO_ENTRANCE: NonNullable<ContextType<typeof PresenceContext>> = {
  id: 'prebuilt-page',
  isPresent: true,
  initial: false,
  register: () => () => {},
};

/**
 * On a pre-built page the content is already visible when React starts, so entrance
 * animations would hide it and fade it back in. The gate turns them off for that first
 * page (on the server and during hydration, keeping both renders identical) and turns
 * them back on as soon as the visitor navigates to another page.
 */
export function EntranceGate({ prebuilt, children }: { prebuilt: boolean; children: ReactNode }) {
  const { pathname } = useLocation();
  const [firstPath] = useState(pathname);
  const navigated = useRef(false);
  if (pathname !== firstPath) navigated.current = true;
  return <PresenceContext.Provider value={prebuilt && !navigated.current ? NO_ENTRANCE : null}>{children}</PresenceContext.Provider>;
}
